import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * Warranty : famille "couverture dans le temps".
 *
 * Couverture attachée à un bien vendu, installé ou réparé, avec un point de
 * départ, une durée et des conditions : garantie légale, garantie
 * constructeur, extension, contrat d'entretien. Construite sur le modèle
 * "famille + type" de la bible (patron : `asset`).
 *
 * Le socle ne connaît pas la nature métier concrète ("garantie constructeur",
 * "extension 2 ans") : il connaît la famille générique `warranty`. Le
 * vocabulaire métier est porté par le champ `type` (chaîne libre déclarée par
 * l'app).
 *
 * Sujet couvert POLYMORPHE, en références optionnelles (aucun import de
 * schéma) : `assetId` si la couverture porte sur un bien suivi (référence
 * `asset`), `orderLineRef` si elle découle d'une ligne de commande vendue
 * (référence vers la ligne d'un `order`). Origine éventuelle par une
 * intervention : `workOrderId` (référence `work-order`, pose ou réparation).
 * Les réclamations ouvertes au titre de la garantie sont pointées par
 * `claimTicketIds` (liste d'ids, références `ticket`).
 *
 * Accès : v0 reste org-wide (même doctrine que asset). Rien de sensible signalé
 * par la fiche.
 *
 * Convention de patron (cf. asset) :
 *   - `xCoreFields`  : les champs MÉTIER seuls (sans les champs standard).
 *   - `XSchema`      : famille complète = champs standard + champs métier.
 *   - `XCreateInputSchema` : ce que l'appelant fournit à la création, VALIDÉ
 *     AVANT toute écriture Firestore.
 */

/** Champs métier de la famille warranty (hors champs standard du socle). */
export const warrantyCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "constructeur", "extension", "legale"). */
  type: z.string().min(1),
  /** Bien couvert (référence `asset`) ; absent si la couverture découle d'une ligne de commande. Optionnel. */
  assetId: z.string().min(1).optional(),
  /** Ligne de commande d'origine (référence vers la ligne d'un `order`) ; absent si la couverture porte sur un asset. Optionnel. */
  orderLineRef: z.string().min(1).optional(),
  /** Intervention à l'origine de la couverture (référence `work-order`, pose ou réparation). Optionnel. */
  workOrderId: z.string().min(1).optional(),
  /** Point de départ de la couverture. Optionnel. */
  startAt: z.date().optional(),
  /** Durée de la couverture, en mois. Optionnel. */
  durationMonths: z.number().int().optional(),
  /** Conditions de la garantie, texte libre (ex. "pièces et main d'oeuvre"). Optionnel. */
  terms: z.string().min(1).optional(),
  /** Réclamations ouvertes au titre de la garantie (liste d'ids, références `ticket`). Optionnel. */
  claimTicketIds: z.array(z.string()).optional(),
} as const;

/** Schéma complet de la famille warranty (champs standard + champs métier). */
export const WarrantySchema = z.object({
  ...baseEntityFields,
  ...warrantyCoreFields,
});

/** Type d'une warranty. */
export type Warranty = z.infer<typeof WarrantySchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const WarrantyCreateInputSchema = z.object({
  ...warrantyCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'une warranty. */
export type WarrantyCreateInput = z.infer<typeof WarrantyCreateInputSchema>;

/**
 * Schema du patch de mise a jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont geres par le SDK : aucun des deux n'est accepte ici.
 */
export const WarrantyUpdateInputSchema = z
  .object({ ...warrantyCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise a jour d'une warranty. */
export type WarrantyUpdateInput = z.infer<typeof WarrantyUpdateInputSchema>;
